/**
 * @fileoverview Route-level permission guard.
 *
 * แก้ปัญหา: user ที่ไม่มี permission ของเมนู ยังสามารถพิมพ์ URL เข้าหน้าได้โดยตรง
 * (เมนูถูกซ่อนแล้วจาก Navbar แต่ route ไม่ได้ตรวจสิทธิ์)
 *
 * วิธีการ: ใช้ "แหล่งความจริงเดียว" (single source of truth) คือ map ของ
 * link -> permission ที่ประกาศไว้แล้วใน global.js (global.menu_admin/customer/
 * warehouse/super_user) มาตรวจสิทธิ์ตอนเข้าถึง route โดยตรง
 *
 * ใช้เป็น layout route ของ react-router v6:
 *   <Route element={<PermissionRoute />}>
 *     <Route path="/ad/dashboard" element={<AdminDashboard />} />
 *     ...
 *   </Route>
 */

import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
// ensure global.menu_* are populated (idempotent import)
import "../global.js";

/**
 * สร้าง map ของ pathname -> permission ที่ต้องใช้ จากนิยามเมนูใน global.js
 * @returns {Object<string,string>}
 */
const buildPermissionMap = () => {
  const map = {};
  const menus = [
    ...(global.menu_admin || []),
    ...(global.menu_customer || []),
    ...(global.menu_warehouse || []),
    ...(global.menu_super_user || []),
  ];
  menus.forEach((item) => {
    if (item.link && item.permission) {
      const path = item.link.split("?")[0]; // ตัด query string ออก
      map[path] = item.permission;
    }
  });
  return map;
};

/**
 * อ่าน profile จาก localStorage (ค่าล่าสุดเสมอ)
 * @returns {Object|null}
 */
const getProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("cm-profile"));
  } catch {
    return null;
  }
};

const PermissionRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem("cm-acc-tk");
  const profile = getProfile();

  // 1) ยังไม่ได้ login -> ไปหน้า login
  if (!token || !profile) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // 2) บัญชีระดับแพลตฟอร์ม/บริษัท ข้ามการตรวจสิทธิ์ระดับหน้า
  //    (สอดคล้องกับการ special-case ที่มีอยู่เดิมใน Navbar/console)
  if (["super_user", "admin-company"].includes(profile.user_type)) {
    return <Outlet />;
  }

  const required = buildPermissionMap()[location.pathname];

  // 3) ไม่มีการประกาศ permission สำหรับ path นี้ -> ปล่อยผ่าน (เช่นหน้าที่ไม่อยู่ในเมนู)
  if (!required) {
    return <Outlet />;
  }

  // 4) ตรวจสิทธิ์จาก permission ของ role
  const perms = profile?.role?.permission || [];
  if (perms.includes(required)) {
    return <Outlet />;
  }

  // 5) ไม่มีสิทธิ์ -> ส่งไปหน้า not found (catch-all route)
  return <Navigate to="/notfound" replace />;
};

export default PermissionRoute;
